import { cloneDeep, debounce, escapeRegExp, throttle } from 'lodash'
import textareaCaret from 'textarea-caret'

import { calculateSmsStats, calculateVoiceStats } from '@sigma-legacy-libs/essentials/lib/utils/smpp'
import { SendingTypes } from '@sigmasms/shared-types'

import { components, labelModes, proxy, services } from '@/utils'

import render from './render'

export default {
  name: 'CatchSymbolsInput',

  mixins: [ proxy({ defaultValue: '' }) ],

  props: {
    tags: {
      type: Array,
      default: () => {
        return []
      }
    },

    component: {
      type: String,
      default: components['text-field']
    },

    customMenu: {
      type: Array,
      default: () => {
        return []
      }
    },

    sendingType: String,

    messagePayloadGenerator: {
      type: Boolean,
      default: false
    },

    utm: {
      type: Boolean,
      default: false
    },

    labelMode: {
      type: String,
      default: labelModes.hidden,
      validator: value => {
        return Object.values(labelModes).includes(value)
      }
    },

    type: String,
    hint: String,
    label: String,

    required: Boolean,
    disabled: Boolean,
    readonly: Boolean,
    rounded: Boolean,
    dense: Boolean,
    stats: Boolean,

    maxLength: Number,
    error: String
  },

  data() {
    return {
      catchInput: document.getElementById(`input-${this._uid}`),

      tag: null,
      section: null,

      focus: false,

      currentOption: -1,

      showDialog: false
    }
  },

  computed: {
    computedText() {
      const proxy = cloneDeep(this.proxy)
      if (proxy) {
        return proxy.replace(this.computedTags.regExpFullTags, '')
      }

      return ''
    },

    $stats() {
      const value = this.proxy || ''
      const result = [ this.getTranslate(`${services.sendings}.statistic.symbols`, { count: value.length }) ]
      switch (this.sendingType) {
        case SendingTypes.sms: {
          const stats = calculateSmsStats(value)
          result.push(this.getTranslate(`${services.sendings}.statistic.parts`, { count: stats.segments }))
          break
        }
        case SendingTypes.voice: {
          const stats = calculateVoiceStats(value)
          result.push(this.getTranslate(`${services.sendings}.statistic.segments`, { count: stats.segments }))
          break
        }
      }

      return result
    },

    computedMenu() {
      if (this.section) {
        return this.section.menu
      }

      return []
    },

    computedTags() {
      const tags = this.tags || []
      const tagsDefinition = {}
      const starts = []
      const ends = []

      for (const tag of tags) {
        if (tag.start && tag.end) {
          const start = escapeRegExp(tag.start)
          const end = escapeRegExp(tag.end)

          let sourceSections = [ true ]
          if (Array.isArray(tag.sections) && tag.sections.length) {
            sourceSections = tag.sections
          }

          tagsDefinition[tag.start] = {
            start: tag.start,
            end: tag.end,
            sections: sourceSections.map(section => {
              if (section === true) {
                return section
              }

              let menuSource = {}
              if (typeof section.menu === 'object') {
                menuSource = section.menu
              }

              return {
                menu: Object.keys(menuSource).map(key => {
                  return {
                    title: section.menu[key],
                    value: key
                  }
                })
              }
            })
          }

          starts.push(start)
          ends.push(end)
        }
      }

      const startsGroup = `(${[ ...starts, '\\|' ].join('|')})`
      const endsOptionalGroup = `((?:${ends.map(t => {
        return `\\${t}`
      }).join('|')}|[^${ends.join('')}])*?)`
      const endsGroup = `(${[ ...ends, '$' ].join('|')})`

      const regExpFullTags = new RegExp(`${startsGroup}${endsOptionalGroup}${endsGroup}`, 'gmi')

      return {
        starts,
        ends,
        tagsDefinition,
        regExpFullTags
      }
    },

    computedPosition() {
      this.getInput()

      let caret = {
        top: 0,
        left: 0,
        height: 0
      }
      let position = 0
      if (this.section) {
        position = this.section.absStart
      }
      if (~position && this.catchInput) {
        caret = textareaCaret(this.catchInput, position)
      }

      return {
        top: caret.top || 0,
        left: caret.left || 0,
        height: (caret.height || 0) + 16
      }
    },

    showMenu() {
      let result = false
      if (!this.focus) {
        return result
      }

      if (this.section) {
        if (this.section.index === 0 || this.section.index === 2) {
          result = !!this.computedMenu.length
        }
      }

      if (!result) {
        this.currentOption = -1
      }

      return result
    }
  },

  mounted() {
    this.getInput()
    this.getTagsFromText()
  },

  beforeDestroy() {
    this.catchInput = undefined
  },

  methods: {
    blurInput() {
      this.getInput()
      this.catchInput.blur()
    },
    focusInput() {
      this.getInput()
      this.catchInput.focus()
    },

    inRange(start, end, selectionStart, selectionEnd) {
      return start <= selectionStart && end >= selectionStart && start <= selectionEnd && end >= selectionEnd
    },

    getTagsFromText: throttle(
      function() {
        this.getInput()

        const selectionStart = this.catchInput?.selectionStart
        const selectionEnd = this.catchInput?.selectionEnd
        const fullText = this.catchInput?.value

        let lastTagEnded = 0
        const tags = (fullText?.match(this.computedTags.regExpFullTags) || []).map(tag => {
          const sections = []

          const start = fullText.indexOf(tag, lastTagEnded)
          const end = start + tag.length
          lastTagEnded = end

          let cursor = 0
          let isOpened = true
          let somethingLeft = true
          let lastSectionEndedWith = ''
          let matches
          let openingTag
          let tagDefinition

          const startsGroup = `(${this.computedTags.starts.join('|')})?`
          const endsOptionalGroup = `((?:${this.computedTags.ends.map(v => {
            return `\\${v}`
          }).join('|')}|[^${this.computedTags.ends.join('')}])*?)`
          const endsGroup = `(?:\\||${this.computedTags.ends.join('|')}|$)`

          const regExpMatchSections = new RegExp(`${startsGroup}${endsOptionalGroup}${endsGroup}`, 'g')

          while (somethingLeft && (matches = regExpMatchSections.exec(tag)) !== null) {
            const sectionContentFull = matches[0]
            const sectionContent = matches[2]
            const lastChar = sectionContentFull.slice(-1)

            if (!openingTag) {
              openingTag = matches[1]
              if (!tagDefinition) {
                tagDefinition = this.computedTags.tagsDefinition[openingTag]
              }
            }

            if (
              sectionContent ||
              !sectionContent && (!lastSectionEndedWith || lastSectionEndedWith === '|' || lastChar === '|')
            ) {
              let insidePosition = 0
              if (~sectionContentFull.indexOf(tagDefinition.start)) {
                insidePosition = tagDefinition.start.length
              }
              const startWithinTag = cursor + insidePosition
              const endWithinTag = startWithinTag + sectionContent.length
              cursor = endWithinTag + (sectionContentFull.length - sectionContent.length - insidePosition)

              sections.push({
                index: sections.length,
                sectionContent,
                start: startWithinTag,
                end: endWithinTag,
                absStart: start + startWithinTag,
                absEnd: start + endWithinTag,
                menu: tagDefinition.sections && tagDefinition.sections[sections.length].menu || []
              })
              lastSectionEndedWith = lastChar
              isOpened = tagDefinition.end !== lastChar || tagDefinition.start === tag
            }

            if (
              !isOpened ||
              !sectionContent && lastChar !== '|' ||
              tagDefinition && tagDefinition.sections.length <= sections.length
            ) {
              somethingLeft = false
            }
          }

          return {
            tag,
            start,
            end,
            sections,
            tagStart: tagDefinition.start,
            tagEnd: tagDefinition.end,
            isOpened
          }
        })

        const tag = tags.find(tag => {
          return this.inRange(
            tag.start + tag.tagStart.length,
            tag.end + tag.tagEnd.length,
            selectionStart,
            selectionEnd
          )
        }) || null

        this.tag = tag
        this.section = tag && tag.sections.find(section => {
          return this.inRange(section.absStart, section.absEnd, selectionStart, selectionEnd)
        }) || null
      },
      150,
      {
        leading: false,
        trailing: true
      }
    ),

    setOption(option) {
      if (option < 0) {
        option = 0
      } else if (option >= this.computedMenu.length - 1) {
        option = this.computedMenu.length - 1
      }

      this.currentOption = option
    },

    insertText(text) {
      this.focusInput()

      let startCaret = this.catchInput.selectionStart
      let endCaret = this.catchInput.selectionEnd
      if (this.section) {
        startCaret = this.section.absStart + 1
        endCaret = this.section.absStart + 1
      }

      let sliceStart = ''
      let sliceEnd = ''
      if (this.proxy) {
        let sliceStartEnd = this.catchInput.selectionEnd
        let sliceEndStart = this.catchInput.selectionEnd
        if (this.section) {
          sliceStartEnd = this.section.absStart
          sliceEndStart = this.section.absEnd
        }

        sliceStart = this.proxy.slice(0, sliceStartEnd)
        sliceEnd = this.proxy.slice(sliceEndStart)
      }

      let tag = ''
      if (this.tag && this.tag.isOpened) {
        tag = this.tag.tagEnd
      }

      this.proxy = sliceStart + text + tag + sliceEnd

      this.$nextTick(() => {
        this.getTagsFromText()
        this.catchInput.selectionStart = startCaret + text.length
        this.catchInput.selectionEnd = endCaret + text.length
      })
    },

    setFocus: debounce(
      function() {
        this.focus = this.catchInput === document.activeElement
      },
      250,
      {
        leading: false,
        trailing: true
      }
    ),

    getInput() {
      if (!this.catchInput) {
        this.catchInput = document.getElementById(`input-${this._uid}`)
      }
    }
  },

  render
}
